import type { TFunction } from 'i18next';

/**
 * Possible states for the Meta advertising card.
 * - 'normal': Card is selectable (integration exists, not Lite tier)
 * - 'locked': Card is locked for Lite tier accounts
 * - 'setup': Card shows setup button (no Meta integration)
 * - 'disabled_monetization': Card is disabled for monetization campaigns
 */
export type TMetaCardState = 'normal' | 'locked' | 'setup' | 'disabled_monetization';

/** Parameters for determining Meta card state */
export interface IGetMetaCardStateParams {
  /** Whether the account is on the Platform Lite tier */
  isPlatformLite: boolean;
  /** Whether the account has an existing Meta integration */
  hasExistingIntegration: boolean;
  /** Whether this is a monetization campaign */
  isMonetization?: boolean;
}

/**
 * Determines the state of the Meta advertising card based on account status.
 *
 * @param params - Object containing account status flags
 * @returns The appropriate Meta card state
 */
export function getMetaCardState(params: IGetMetaCardStateParams): TMetaCardState {
  const { isPlatformLite, hasExistingIntegration, isMonetization } = params;

  if (isMonetization) {
    return 'disabled_monetization';
  }

  if (isPlatformLite) {
    return 'locked';
  }

  if (!hasExistingIntegration) {
    return 'setup';
  }

  return 'normal';
}

/**
 * Returns the tooltip text for a locked Meta card.
 *
 * @param t - Translation function
 * @returns Translated tooltip message
 */
export function getMetaLockedTooltip(t: TFunction): string {
  return t('Upgrade to Essential to access this feature.');
}

/**
 * Returns the description text for the Meta card when setup is required.
 *
 * @param t - Translation function
 * @returns Translated description message
 */
export function getMetaSetupDescription(t: TFunction): string {
  return t(
    'Integrate with Meta in order to advertise on Facebook and Instagram. Once connected, return here to finish setting up.',
  );
}

/**
 * Returns the default description text for the Meta card.
 *
 * @param t - Translation function
 * @returns Translated description message
 */
export function getMetaDefaultDescription(t: TFunction): string {
  return t('Connect with users on Facebook, Instagram, and other Meta platforms');
}

/**
 * Returns the tooltip text for a Meta card disabled due to monetization.
 *
 * @param t - Translation function
 * @returns Translated tooltip message
 */
export function getMetaMonetizationTooltip(t: TFunction): string {
  return t('Meta advertising is not available for monetization campaigns.');
}

/**
 * Returns the appropriate tooltip for the Meta card based on its state.
 *
 * @param metaCardState - Current state of the Meta card
 * @param t - Translation function
 * @returns Tooltip text for locked/disabled states, undefined for normal/setup
 */
export function getMetaTooltip(metaCardState: TMetaCardState, t: TFunction): string | undefined {
  if (metaCardState === 'locked') {
    return getMetaLockedTooltip(t);
  }
  if (metaCardState === 'disabled_monetization') {
    return getMetaMonetizationTooltip(t);
  }
  return undefined;
}

/**
 * Returns the tooltip text when Display channel has existing creatives.
 *
 * @param t - Translation function
 * @returns Translated tooltip message
 */
export function getDisplayCreativesTooltip(t: TFunction): string {
  return t('Delete Display creatives to deselect Display channel.');
}

/**
 * Returns the tooltip text when Meta channel has existing creatives.
 *
 * @param t - Translation function
 * @returns Translated tooltip message
 */
export function getMetaCreativesTooltip(t: TFunction): string {
  return t('Delete Meta creatives to deselect Meta channel.');
}
